import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, User, Mail, Paperclip, FileText, AlertTriangle, CheckCircle, PauseCircle, CalendarClock, Upload, X, Building2, Trash2, Star, BellRing } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { useTicket, useAssignTicket, useTransitionTicket, useUpdateTicket, useTicketEmails, useTicketSla, useDeleteTicket } from '../../hooks/use-tickets';
import { useUsers } from '../../hooks/use-users';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, PRIORITY_DESCRIPTIONS, WORKFLOW_TRANSITIONS, ALL_PRIORITIES, formatTicketId } from '../../lib/constants';
import { formatDateTime } from '../../lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import type { Comment, DevelopmentLogEntry, TicketPriority } from '../../types';
import { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { AttachmentPreview } from '../../components/ui/AttachmentPreview';
import { TicketProgress } from '../../components/ui/TicketProgress';
import { Dialog } from '../../components/ui/dialog';
import { DevelopmentLogTable } from '../../components/DevelopmentLogTable';
import { useToast } from '../../components/ui/toast';
import { useAuthStore } from '../../stores/auth-store';
import { useProjects, useProjectSubcategories } from '../../hooks/use-projects';

export function AdminTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useTicket(id!);
  const ticket = data?.data as (NonNullable<typeof data>['data'] & { contractName?: string; projectName?: string; clientName?: string }) | undefined;
  const { toast } = useToast();
  const assignTicket = useAssignTicket({
    onSuccess: () => toast('success', 'Alocare salvată cu succes'),
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });
  const transition = useTransitionTicket({
    onSuccess: () => toast('success', 'Tichetul a fost actualizat'),
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });
  const updateTicket = useUpdateTicket({
    onSuccess: () => toast('success', 'Salvat cu succes'),
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });
  const deleteTicket = useDeleteTicket();
  const { data: agentUsersData } = useUsers({ role: 'AGENT', pageSize: 100 });
  const { data: adminUsersData } = useUsers({ role: 'ADMIN', pageSize: 100 });
  const agents = [...(agentUsersData?.data ?? []), ...(adminUsersData?.data ?? [])];
  const { data: emailsData } = useTicketEmails(id!);
  const emails = emailsData?.data ?? [];
  const { data: slaData } = useTicketSla(id!);
  const sla = slaData?.data;
  const currentUser = useAuthStore((s) => s.user);

  const [assignAgentId, setAssignAgentId] = useState('');
  const [assignSupervisorId, setAssignSupervisorId] = useState('');
  const [emailPreview, setEmailPreview] = useState<{ subject: string; htmlBody: string } | null>(null);
  const [estHours, setEstHours] = useState('');
  const [devDescription, setDevDescription] = useState('');
  const [respJustification, setRespJustification] = useState('');
  const [resolJustification, setResolJustification] = useState('');
  const [justificationLoaded, setJustificationLoaded] = useState(false);

  // Priority & project edit state
  const [editingPriority, setEditingPriority] = useState(false);
  const [newPriority, setNewPriority] = useState<TicketPriority | ''>('');
  const [priorityJustification, setPriorityJustification] = useState('');
  const [editingProject, setEditingProject] = useState(false);
  const [newContractId, setNewContractId] = useState('');
  const [newSubcategoryId, setNewSubcategoryId] = useState('');
  const { data: projectsData } = useProjects({ pageSize: 100 });
  const allContracts = projectsData?.data ?? [];
  const { data: subcatsData } = useProjectSubcategories(newContractId || (ticket?.projectId ?? ''));
  const subcategories = subcatsData?.data ?? [];

  const { data: commentsData } = useQuery({
    queryKey: ['comments', id],
    queryFn: () => api.get<Comment[]>(`/tickets/${id}/comments`),
    enabled: !!id,
  });
  const comments = commentsData?.data ?? [];

  const [newComment, setNewComment] = useState('');
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Current (possibly unsaved) rows of the "Ore dezvoltare" table, kept in sync
  // by DevelopmentLogTable so they can be auto-saved before "Trimite la review"
  // (sugestie cmuy5xdlr).
  const devRowsRef = useRef<DevelopmentLogEntry[] | null>(null);

  // "Trimite la review" popup — asks whether to attach a comment (public or internal)
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewCommentType, setReviewCommentType] = useState<'public' | 'internal'>('public');

  // "Trimite reminder client" — manual reminder email while WAITING_CLIENT (sugestie cmuf41x8)
  const [remindModalOpen, setRemindModalOpen] = useState(false);
  const remindClient = useMutation({
    mutationFn: () => api.post(`/tickets/${id}/remind-client`),
    onSuccess: () => {
      setRemindModalOpen(false);
      qc.invalidateQueries({ queryKey: ['tickets', id, 'emails'] });
      toast('success', 'Reminder trimis către client');
    },
    onError: (e) => toast('error', `Eroare la trimiterea reminderului: ${e.message}`),
  });
  const addComment = useMutation({
    mutationFn: async ({ internal }: { internal: boolean }) => {
      await api.post(`/tickets/${id}/comments`, { body: newComment, isInternal: internal });
      if (commentFiles.length > 0) {
        setUploading(true);
        for (const file of commentFiles) {
          const fd = new FormData();
          fd.append('ticketId', id!);
          fd.append('file', file);
          await api.upload('/attachments', fd);
        }
        setUploading(false);
      }
      // When sending to client, also transition to WAITING_CLIENT
      if (!internal && ticket && ticket.status !== 'CLOSED' && ticket.status !== 'WAITING_CLIENT') {
        await api.post(`/tickets/${id}/transition`, { action: 'ask_client' });
      }
    },
    onSuccess: (_data, variables) => {
      setNewComment('');
      setCommentFiles([]);
      qc.invalidateQueries({ queryKey: ['comments', id] });
      qc.invalidateQueries({ queryKey: ['tickets', id] });
      toast('success', variables.internal ? 'Comentariu intern salvat' : 'Comentariu trimis către client');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  useEffect(() => {
    if (!ticket) return;
    setAssignAgentId(ticket.assignedToId ?? '');
    setAssignSupervisorId(ticket.supervisorId ?? '');
  }, [ticket?.id]);

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  if (!ticket) return <p className="text-center text-gray-500">Tichetul nu a fost găsit</p>;

  // Load justification from ticket on first render
  if (ticket && !justificationLoaded) {
    if (ticket.responseJustification) setRespJustification(ticket.responseJustification);
    if (ticket.resolutionJustification) setResolJustification(ticket.resolutionJustification);
    setJustificationLoaded(true);
  }

  const currentTransitions = (WORKFLOW_TRANSITIONS[ticket.status] ?? []).filter((transition) => transition.action !== 'assign');
  const assignmentChanged = assignAgentId !== (ticket.assignedToId ?? '') || assignSupervisorId !== (ticket.supervisorId ?? '');

  const handleAssignment = () => {
    if (!assignAgentId) return;
    if (!assignSupervisorId) {
      alert('Supervizorul este obligatoriu la alocare');
      return;
    }
    assignTicket.mutate({ id: id!, assignedToId: assignAgentId, supervisorId: assignSupervisorId });
  };

  // F5: validate & auto-save dev fields before IN_PROGRESS → review (used by the review popup).
  // The dev-log table rows are auto-saved first (sugestie cmuy5xdlr), then the
  // per-ticket fields; developmentDuration comes from the table (auto-summed server-side).
  const saveDevFieldsBeforeReview = async (): Promise<boolean> => {
    // 1. Auto-save the "Ore dezvoltare" table rows so nothing is lost when the
    //    agent forgot to press "Salvează ore" (sugestie cmuy5xdlr).
    const currentRows = devRowsRef.current;
    if (currentRows && currentRows.length > 0) {
      for (const r of currentRows) {
        if (!r.logDate) {
          toast('error', 'Fiecare rând din tabelul de ore dezvoltare are nevoie de o dată');
          return false;
        }
        if (!r.duration || r.duration <= 0) {
          toast('error', 'Fiecare rând din tabelul de ore dezvoltare are nevoie de o durată pozitivă (ore)');
          return false;
        }
      }
      try {
        await api.put(`/tickets/development-logs/${id}`, { entries: currentRows });
        devRowsRef.current = null;
        qc.invalidateQueries({ queryKey: ['tickets', id, 'development-logs'] });
      } catch (e) {
        toast('error', `Eroare la salvarea orelor de dezvoltare: ${(e as Error).message}`);
        return false;
      }
    }
    // 2. Validate + save the remaining dev fields on the ticket.
    const dur = ticket.developmentDuration;
    const desc = devDescription || ticket.developmentDescription;
    if (dur == null || dur <= 0) {
      alert('Adaugă cel puțin un rând în tabelul de ore dezvoltare înainte de a trimite la review');
      return false;
    }
    if (!desc) {
      alert('Descrierea dezvoltare este obligatorie înainte de a trimite la review');
      return false;
    }
    const payload: Record<string, unknown> = {};
    if (devDescription) payload.developmentDescription = devDescription;
    if (estHours) payload.estimatedTime = parseFloat(estHours);
    if (Object.keys(payload).length > 0) {
      try {
        await api.patch(`/tickets/${id}`, payload);
      } catch (e) {
        toast('error', `Eroare la salvarea datelor de dezvoltare: ${(e as Error).message}`);
        return false;
      }
    }
    return true;
  };

  const confirmReviewTransition = async () => {
    setReviewModalOpen(false);
    if (!(await saveDevFieldsBeforeReview())) return;
    const body = reviewComment.trim();
    try {
      const res = await transition.mutateAsync({
        id: id!,
        action: 'review',
        ...(body ? { commentBody: body, commentIsInternal: reviewCommentType === 'internal' } : {}),
      });
      // Attach the files from the comment box to the comment created by the
      // transition, so nothing is lost on "Trimite la review" (sugestie cmuqnh0vn).
      const commentId = (res.data as { transitionCommentId?: string | null } | undefined)?.transitionCommentId ?? null;
      if (commentFiles.length > 0) {
        setUploading(true);
        try {
          for (const file of commentFiles) {
            const fd = new FormData();
            fd.append('ticketId', id!);
            if (commentId) fd.append('commentId', commentId);
            fd.append('file', file);
            await api.upload('/attachments', fd);
          }
          setCommentFiles([]);
          qc.invalidateQueries({ queryKey: ['comments', id] });
        } catch {
          // Tranziția a reușit, dar upload-ul a eșuat: fișierele rămân în casetă
          // pentru reîncercare (sugestie cmuqnh0vn).
          toast('error', 'Comentariul a fost salvat, dar atașamentele nu au putut fi încărcate. Reîncearcă.');
        } finally {
          setUploading(false);
        }
      }
      if (body) setNewComment('');
    } catch (e) {
      // The transition failed — keep the files in the box for retry (sugestie cmuqnh0vn).
      toast('error', `Eroare la trimiterea la review: ${(e as Error).message}`);
    }
  };

  const handleTransition = async (action: string) => {
    // "Trimite la review" — open the comment popup first (sugestie: comentariu salvat automat la review)
    if (action === 'review') {
      setReviewComment(newComment);
      setReviewCommentType('public');
      setReviewModalOpen(true);
      return;
    }

    // F4: validate & auto-save estimation at ESTIMATING → start_work
    if (ticket && ticket.status === 'ENCORSA_ESTIMATING' && action === 'start_work') {
      const hours = estHours ? parseFloat(estHours) : ticket.estimatedTime;
      if (hours == null) {
        alert('Ore estimate sunt obligatorii înainte de a pune în lucru');
        return;
      }
      // Auto-save estimation values before transitioning
      const payload: Record<string, unknown> = {};
      if (estHours) payload.estimatedTime = parseFloat(estHours);
      if (Object.keys(payload).length > 0) {
        try {
          await api.patch(`/tickets/${id}`, payload);
        } catch (e) {
          toast('error', `Eroare la salvarea estimării: ${(e as Error).message}`);
          return;
        }
      }
    }

    // F5: validate & auto-save dev fields at SUPERVISOR_REVIEW → approve
    if (ticket && ticket.status === 'ENCORSA_SUPERVISOR_REVIEW' && action === 'approve') {
      const dur = ticket.developmentDuration;
      const desc = devDescription || ticket.developmentDescription;
      if (dur == null || dur <= 0) {
        alert('Adaugă cel puțin un rând în tabelul de ore dezvoltare înainte de a aproba');
        return;
      }
      if (!desc) {
        alert('Descrierea dezvoltare este obligatorie înainte de a aproba');
        return;
      }
      const payload: Record<string, unknown> = {};
      if (devDescription) payload.developmentDescription = devDescription;
      if (estHours) payload.estimatedTime = parseFloat(estHours);
      if (Object.keys(payload).length > 0) {
        try {
          await api.patch(`/tickets/${id}`, payload);
        } catch (e) {
          toast('error', `Eroare la salvarea datelor de dezvoltare: ${(e as Error).message}`);
          return;
        }
      }
    }

    transition.mutate({ id: id!, action });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/admin/tickets" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text">
          <ArrowLeft size={16} /> Înapoi
        </Link>
        {currentUser?.role === 'ADMIN' && (
          <Button
            variant="danger"
            size="sm"
            disabled={deleteTicket.isPending}
            onClick={() => {
              if (!confirm(`Sigur vrei să ștergi tichetul #${formatTicketId(ticket.numericId)}? Această acțiune este ireversibilă.`)) return;
              deleteTicket.mutate(id!, { onSuccess: () => navigate('/admin/tickets') });
            }}
          >
            <Trash2 size={16} /> {deleteTicket.isPending ? 'Se șterge...' : 'Șterge'}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500 dark:bg-dark-border dark:text-dark-muted">#{formatTicketId(ticket.numericId)}</span>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">{ticket.subject}</h1>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-dark-muted">
            {ticket.clientName && <span className="flex items-center gap-1"><Building2 size={14} /> {ticket.clientName}</span>}
            <span className="flex items-center gap-1"><User size={14} /> {ticket.createdBy?.name ?? '—'}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {formatDateTime(ticket.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Badge className={STATUS_COLORS[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
            {editingPriority ? (
              <div className="flex flex-col items-end gap-2 min-w-[220px]">
                <Select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as TicketPriority)}
                  label=""
                  options={ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
                />
                <Textarea
                  placeholder="Justificarea modificării priorității (obligatorie)..."
                  value={priorityJustification}
                  onChange={(e) => setPriorityJustification(e.target.value)}
                  className="text-xs"
                />
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    disabled={!newPriority || newPriority === ticket.priority || !priorityJustification.trim() || updateTicket.isPending}
                    onClick={() => {
                      updateTicket.mutate({ id: id!, priority: newPriority, priorityChangeJustification: priorityJustification } as any, {
                        onSuccess: () => { setEditingPriority(false); setPriorityJustification(''); },
                      });
                    }}
                  >
                    Salvează
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingPriority(false); setNewPriority(''); setPriorityJustification(''); }}>
                    Anulează
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="cursor-pointer hover:opacity-80"
                onClick={() => { setEditingPriority(true); setNewPriority(ticket.priority); }}
                title="Click pentru a modifica prioritatea"
              >
                <Badge className={PRIORITY_COLORS[ticket.priority]}>
                  {PRIORITY_LABELS[ticket.priority]} ✎
                </Badge>
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-muted italic max-w-xs text-right">{PRIORITY_DESCRIPTIONS[ticket.priority]}</p>
          {ticket.priorityChangeJustification && (
            <p className="text-xs text-amber-600 dark:text-amber-400 italic max-w-xs text-right">
              Justificare prioritate: {ticket.priorityChangeJustification}
            </p>
          )}
        </div>
      </div>

      {/* Ticket progress breadcrumb */}
      <Card>
        <CardContent>
          <TicketProgress currentStatus={ticket.status} statusHistory={ticket.statusHistory} view="admin" />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader><h2 className="font-semibold">Descriere</h2></CardHeader>
            <CardContent><p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-dark-text">{ticket.description}</p></CardContent>
          </Card>

          {/* Contract / Project / Attachments */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2"><FileText size={16} /> Contract & Proiect</h2>
                {!editingProject && (
                  <Button size="sm" variant="outline" onClick={() => { setEditingProject(true); setNewContractId(ticket.projectId ?? ''); setNewSubcategoryId(ticket.subcategoryId ?? ''); }}>
                    Modifică
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {editingProject ? (
                <div className="space-y-3">
                  <Select
                    value={newContractId}
                    onChange={(e) => { setNewContractId(e.target.value); setNewSubcategoryId(''); }}
                    label="Contract"
                    options={[
                      { value: '', label: 'Selectează contractul' },
                      ...allContracts.map((c) => ({ value: String(c.elementId), label: c.name })),
                    ]}
                  />
                  <Select
                    value={newSubcategoryId}
                    onChange={(e) => setNewSubcategoryId(e.target.value)}
                    label="Proiect / Subcategorie"
                    disabled={!newContractId}
                    options={[
                      { value: '', label: newContractId ? 'Selectează proiectul' : 'Selectează mai întâi contractul' },
                      ...subcategories.map((s) => ({ value: String(s.elementId), label: s.name })),
                    ]}
                  />
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      disabled={!newContractId || !newSubcategoryId || updateTicket.isPending}
                      onClick={() => {
                        updateTicket.mutate({ id: id!, projectId: newContractId, subcategoryId: newSubcategoryId } as any, {
                          onSuccess: () => setEditingProject(false),
                        });
                      }}
                    >
                      Salvează
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingProject(false)}>
                      Anulează
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-dark-muted">Contract</span>
                    <span className="font-medium dark:text-dark-text">{ticket.contractName ?? ticket.projectId ?? '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-dark-muted">Proiect / Subcategorie</span>
                    <span className="font-medium dark:text-dark-text">{ticket.projectName ?? ticket.subcategoryId ?? '—'}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (() => {
            const clientAtts = ticket.attachments!.filter((a) => a.uploadedBy?.role === 'CLIENT');
            const encorsaAtts = ticket.attachments!.filter((a) => a.uploadedBy?.role !== 'CLIENT');
            return (
              <Card>
                <CardHeader><h2 className="font-semibold flex items-center gap-2"><Paperclip size={16} /> Atașamente ({ticket.attachments!.length})</h2></CardHeader>
                <CardContent className="space-y-4">
                  {clientAtts.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-dark-muted">Adăugate de client</h3>
                      <div className="space-y-2">
                        {clientAtts.map((att) => (
                          <AttachmentPreview key={att.id} attachment={att} />
                        ))}
                      </div>
                    </div>
                  )}
                  {encorsaAtts.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-dark-muted">Adăugate de Encorsa</h3>
                      <div className="space-y-2">
                        {encorsaAtts.map((att) => (
                          <AttachmentPreview key={att.id} attachment={att} />
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Comments */}
          <Card>
            <CardHeader><h2 className="font-semibold">Comentarii ({comments.length})</h2></CardHeader>
            <div className="divide-y">
              {comments.map((c) => (
                <div key={c.id} className={`px-6 py-4 ${c.isInternal ? 'bg-yellow-50 border-l-4 border-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-600' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium dark:text-dark-text">{c.author.name} {c.isInternal && <Badge className="ml-1 text-xs bg-yellow-200 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">Intern</Badge>}</span>
                    <span className="text-xs text-gray-400 dark:text-dark-muted">{formatDateTime(c.createdAt)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-dark-text">{c.body}</p>
                </div>
              ))}
              {comments.length === 0 && <p className="px-6 py-4 text-sm text-gray-400 dark:text-dark-muted">Niciun comentariu</p>}
            </div>
            {/* Hide comment form from agents at SUPERVISOR_REVIEW — only supervisor/admin can comment */}
            {ticket.status === 'CLOSED' ? (
              <div className="border-t px-6 py-4 space-y-3">
                <p className="text-sm text-gray-500 dark:text-dark-muted">Ticketul este închis. Redeschideți pentru a adăuga comentarii.</p>
                {ticket.csatRatings && ticket.csatRatings.length > 0 && (() => {
                  const csat = ticket.csatRatings[0];
                  return (
                    <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-dark-text">Rating client:</span>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} size={16} className={s <= csat.score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'} />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">{csat.score}/5</span>
                      </div>
                      {csat.comment && <p className="text-sm text-gray-600 dark:text-dark-muted mt-1">{csat.comment}</p>}
                    </div>
                  );
                })()}
              </div>
            ) : !(ticket.status === 'ENCORSA_SUPERVISOR_REVIEW' && currentUser?.id !== ticket.supervisorId && currentUser?.role !== 'ADMIN') && (
            <div className="border-t px-6 py-4 space-y-2">
              <Textarea placeholder="Adaugă un comentariu..." value={newComment} onChange={(e) => setNewComment(e.target.value)} />
              <div>
                <label className="inline-flex items-center gap-1.5 cursor-pointer rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-dark-muted dark:hover:bg-dark-border">
                  <Upload size={14} />
                  Atașează fișiere
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.heic,.heif,.gif,.svg,.webp,.pdf,.docx,.xlsx,.pptx,.txt,.zip"
                    className="hidden"
                    onChange={(e) => setCommentFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])}
                  />
                </label>
                {commentFiles.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {commentFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-dark-border dark:text-dark-text">
                        {f.name}
                        <button type="button" onClick={() => setCommentFiles((prev) => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 dark:text-dark-muted">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" variant="outline" disabled={!newComment.trim() || addComment.isPending || uploading} onClick={() => addComment.mutate({ internal: true })}>
                  {uploading ? 'Se încarcă...' : 'Comentariu intern'}
                </Button>
                <Button size="sm" disabled={!newComment.trim() || addComment.isPending || uploading} onClick={() => addComment.mutate({ internal: false })}>
                  {uploading ? 'Se încarcă...' : 'Solicită informații de la client'}
                </Button>
              </div>
            </div>
            )}
          </Card>

          {/* Email Log */}          <Card>
            <CardHeader><h2 className="font-semibold flex items-center gap-2"><Mail size={16} /> Jurnal emailuri ({emails.length})</h2></CardHeader>
            <CardContent>
              {emails.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-dark-muted">Niciun email trimis</p>
              ) : (
                <div className="divide-y">
                  {emails.map((e) => (
                    <div key={e.id} className="flex items-center justify-between py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium dark:text-dark-text">{e.recipientName ?? e.recipientEmail}</span>
                        {e.actionName && <Badge className="ml-2 text-xs bg-gray-100 text-gray-600 dark:bg-dark-border dark:text-dark-muted">{e.actionName}</Badge>}
                        <span className="ml-2 text-gray-500 dark:text-dark-muted">— {e.subject}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 dark:text-dark-muted">{formatDateTime(e.sentAt)}</span>
                        <Button size="sm" variant="outline" onClick={() => setEmailPreview({ subject: e.subject, htmlBody: e.htmlBody })}>
                          Preview
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><h2 className="font-semibold text-sm">{ticket.assignedToId ? 'Reasignare' : 'Alocare'}</h2></CardHeader>
            <CardContent className="space-y-3">
              <Select
                value={assignAgentId}
                onChange={(e) => setAssignAgentId(e.target.value)}
                label="Agent"
                options={[{ value: '', label: 'Selectează agent' }, ...agents.map((a) => ({ value: a.id, label: a.name }))]}
              />
              <Select
                value={assignSupervisorId}
                onChange={(e) => setAssignSupervisorId(e.target.value)}
                label="Supervizor *"
                options={[{ value: '', label: 'Selectează supervizor' }, ...agents.map((a) => ({ value: a.id, label: a.name }))]}
              />
              <div className="rounded-2xl border border-[color:var(--surface-line)] bg-[color:var(--surface-panel-strong)] px-3 py-2 text-xs text-[color:var(--ink-muted)]">
                <p>Agent curent: <span className="font-medium text-[color:var(--ink-strong)]">{ticket.assignedTo?.name ?? 'Nealocat'}</span></p>
                <p className="mt-1">Supervizor curent: <span className="font-medium text-[color:var(--ink-strong)]">{ticket.supervisor?.name ?? 'Nealocat'}</span></p>
              </div>
              <Button
                className="w-full"
                disabled={assignTicket.isPending || !assignAgentId || !assignSupervisorId || !assignmentChanged}
                onClick={handleAssignment}
              >
                {assignTicket.isPending ? 'Se salvează...' : ticket.assignedToId ? 'Salvează reasignarea' : 'Alocă ticketul'}
              </Button>
            </CardContent>
          </Card>

          {/* Estimation fields — editable at ESTIMATING and until RESOLVED */}
          {(() => {
            const isReviewAndNotReviewer = ticket.status === 'ENCORSA_SUPERVISOR_REVIEW' && currentUser?.id !== ticket.supervisorId && currentUser?.role !== 'ADMIN';
            const canEdit = ['ENCORSA_ESTIMATING', 'ENCORSA_IN_PROGRESS', 'ENCORSA_SUPERVISOR_REVIEW', 'WAITING_CLIENT', 'RESOLVED', 'CLOSED'].includes(ticket.status) && !isReviewAndNotReviewer;
            // Agentul alocat poate înregistra ore și în WAITING_CLIENT (sugestie cmu2n29m9)
            const canEditDevDuration = (
              (['ENCORSA_IN_PROGRESS', 'WAITING_CLIENT'].includes(ticket.status) && currentUser?.id === ticket.assignedToId) ||
              (ticket.status === 'ENCORSA_SUPERVISOR_REVIEW' && (currentUser?.id === ticket.supervisorId || currentUser?.role === 'ADMIN')) ||
              currentUser?.role === 'ADMIN'
            ) && ['ENCORSA_IN_PROGRESS', 'ENCORSA_SUPERVISOR_REVIEW', 'WAITING_CLIENT', 'RESOLVED', 'CLOSED'].includes(ticket.status);
            const showEstimation = canEdit || ticket.estimatedTime != null || ticket.developmentDuration != null || ticket.developmentDescription != null;
            if (!showEstimation) return null;
            return (
              <Card>
                <CardHeader><h2 className="font-semibold text-sm flex items-center gap-2"><CalendarClock size={14} /> Estimare</h2></CardHeader>
                <CardContent className="space-y-3">
                  {canEdit ? (
                    <>
                      <div>
                        <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Ore estimate de lucru</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          placeholder="ex: 8"
                          value={estHours || (ticket.estimatedTime != null ? String(ticket.estimatedTime) : '')}
                          onChange={(e) => setEstHours(e.target.value)}
                          onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                        />
                      </div>
                      {/* Tabel ore dezvoltare — totalul (developmentDuration) e suma automată a rândurilor */}
                      <DevelopmentLogTable
                        ticketId={id!}
                        editable={canEditDevDuration}
                        total={ticket.developmentDuration}
                        rowsRef={devRowsRef}
                      />
                      {canEditDevDuration ? (
                        <div>
                          <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Descriere dezvoltare</label>
                          <Textarea
                            placeholder="Descriere a activităților de dezvoltare..."
                            value={devDescription || (ticket.developmentDescription ?? '')}
                            onChange={(e) => setDevDescription(e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      ) : (
                        /* Read-only: values stay visible in later steps even when the viewer can no longer edit them */
                        <>
                          {ticket.developmentDescription != null && (
                            <div className="text-sm">
                              <span className="text-gray-500 dark:text-dark-muted">Descriere dezvoltare</span>
                              <p className="mt-1 whitespace-pre-wrap dark:text-dark-text">{ticket.developmentDescription}</p>
                            </div>
                          )}
                        </>
                      )}
                      {/* Hide save button at ESTIMATING (auto-saves on transition), show at other editable steps */}
                      {ticket.status !== 'ENCORSA_ESTIMATING' && (
                        <Button
                          size="sm"
                          className="w-full"
                          disabled={updateTicket.isPending}
                          onClick={() => {
                            const payload: Record<string, unknown> = { id: id! };
                            if (estHours) payload.estimatedTime = parseFloat(estHours);
                            if (devDescription && canEditDevDuration) payload.developmentDescription = devDescription;
                            updateTicket.mutate(payload as any);
                          }}
                        >
                          Salvează
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-dark-muted">Ore estimate</span>
                        <span className="dark:text-dark-text">{ticket.estimatedTime ?? '—'}</span>
                      </div>
                      {ticket.developmentDuration != null && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-dark-muted">Durata dezvoltare</span>
                          <span className="dark:text-dark-text">{ticket.developmentDuration} ore</span>
                        </div>
                      )}
                      {ticket.developmentDescription != null && (
                        <div className="text-sm">
                          <span className="text-gray-500 dark:text-dark-muted">Descriere dezvoltare</span>
                          <p className="mt-1 whitespace-pre-wrap dark:text-dark-text">{ticket.developmentDescription}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Workflow Actions */}
          {(() => {
            const isReviewStep = ticket.status === 'ENCORSA_SUPERVISOR_REVIEW';
            const canActOnReview = currentUser?.id === ticket.supervisorId || currentUser?.role === 'ADMIN';
            if (!isReviewStep && currentTransitions.length === 0) {
              return null;
            }
            return (
              <Card>
                <CardHeader><h2 className="font-semibold text-sm">Acțiuni</h2></CardHeader>
                <CardContent className="space-y-2">
                  {isReviewStep && !canActOnReview ? (
                    <p className="text-xs text-gray-500 dark:text-dark-muted">
                      Ticketul este alocat la reviewer-ul <strong>{ticket.supervisor?.name ?? '—'}</strong>. Doar acesta sau un admin poate efectua acțiuni.
                    </p>
                  ) : (
                    currentTransitions.map((t) => (
                      <Button
                        key={t.action}
                        variant={t.variant}
                        className="w-full"
                        disabled={transition.isPending || assignTicket.isPending}
                        onClick={() => handleTransition(t.action)}
                      >
                        {t.label}
                      </Button>
                    ))
                  )}
                  {ticket.status === 'WAITING_CLIENT' && (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={remindClient.isPending}
                      onClick={() => setRemindModalOpen(true)}
                    >
                      <BellRing size={14} className="mr-1.5" />
                      {remindClient.isPending ? 'Se trimite...' : 'Trimite reminder client'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Info */}
          <Card>
            <CardHeader><h2 className="font-semibold text-sm">Detalii</h2></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {ticket.clientName && <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Client</span><span className="font-medium dark:text-dark-text">{ticket.clientName}</span></div>}
              <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Creat de</span><span className="dark:text-dark-text">{ticket.createdBy?.name ?? '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Creat</span><span className="dark:text-dark-text">{formatDateTime(ticket.createdAt)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Actualizat</span><span className="dark:text-dark-text">{formatDateTime(ticket.updatedAt)}</span></div>
              {ticket.assignedTo && <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Agent</span><span className="dark:text-dark-text">{ticket.assignedTo.name}</span></div>}
              {ticket.supervisor && <div className="flex justify-between"><span className="text-gray-500 dark:text-dark-muted">Supervizor</span><span className="dark:text-dark-text">{ticket.supervisor.name}</span></div>}
            </CardContent>
          </Card>

          {/* SLA */}
          {sla && (
            <Card>
              <CardHeader><h2 className="font-semibold text-sm flex items-center gap-2"><Clock size={14} /> SLA</h2></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {sla.policyName ? (
                  <>
                    <div className="text-xs text-gray-400 dark:text-dark-muted">Politică: {sla.policyName}</div>
                    {/* Response time */}
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-dark-muted">Timp răspuns</span>
                        {sla.responseMet === true && <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle size={14} /> Respectat</span>}
                        {sla.responseMet === false && (
                          ticket.responseJustification
                            ? <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><CheckCircle size={14} /> Justificat</span>
                            : <span className="flex items-center gap-1 text-red-600 dark:text-red-400"><AlertTriangle size={14} /> Depășit{sla.firstResponseMinutes != null && ` (limita: ${sla.firstResponseMinutes} min)`}</span>
                        )}
                        {sla.responseMet === null && <span className="text-gray-400 dark:text-dark-muted">{sla.elapsedResponseMinutes} / {sla.firstResponseMinutes} min</span>}
                      </div>
                      {sla.firstResponseMinutes !== null && sla.responseMet === null && (
                        <div className="mt-1 h-1.5 rounded-full bg-gray-200 dark:bg-dark-border">
                          <div
                            className={`h-full rounded-full ${sla.elapsedResponseMinutes > sla.firstResponseMinutes ? 'bg-red-500' : sla.elapsedResponseMinutes > sla.firstResponseMinutes * 0.8 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, (sla.elapsedResponseMinutes / sla.firstResponseMinutes) * 100)}%` }}
                          />
                        </div>
                      )}
                      {sla.responseMet === false && (
                        <div className="mt-2 space-y-1">
                          <Textarea
                            placeholder="Justificare depășire timp răspuns..."
                            value={respJustification}
                            onChange={(e) => setRespJustification(e.target.value)}
                            className="text-xs"
                          />
                          <Button
                            size="sm"
                            className="w-full"
                            disabled={!respJustification.trim() || updateTicket.isPending}
                            onClick={() => updateTicket.mutate({ id: id!, responseJustification: respJustification })}
                          >
                            {ticket.responseJustification ? 'Actualizează justificarea' : 'Salvează justificarea'}
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Resolution time */}
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-dark-muted">Timp rezolvare</span>
                        {sla.resolutionMet === true && <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle size={14} /> Respectat</span>}
                        {sla.resolutionMet === false && (
                          ticket.resolutionJustification
                            ? <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><CheckCircle size={14} /> Justificat</span>
                            : <span className="flex items-center gap-1 text-red-600 dark:text-red-400"><AlertTriangle size={14} /> Depășit{sla.resolutionMinutes != null && ` (limita: ${sla.resolutionMinutes} min)`}</span>
                        )}
                        {sla.resolutionMet === null && <span className="text-gray-400 dark:text-dark-muted">{sla.elapsedResolutionMinutes} / {sla.resolutionMinutes} min</span>}
                      </div>
                      {sla.resolutionMinutes !== null && sla.resolutionMet === null && (
                        <div className="mt-1 h-1.5 rounded-full bg-gray-200 dark:bg-dark-border">
                          <div
                            className={`h-full rounded-full ${sla.elapsedResolutionMinutes > sla.resolutionMinutes ? 'bg-red-500' : sla.elapsedResolutionMinutes > sla.resolutionMinutes * 0.8 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, (sla.elapsedResolutionMinutes / sla.resolutionMinutes) * 100)}%` }}
                          />
                        </div>
                      )}
                      {sla.resolutionMet === false && (
                        <div className="mt-2 space-y-1">
                          <Textarea
                            placeholder="Justificare depășire timp rezolvare..."
                            value={resolJustification}
                            onChange={(e) => setResolJustification(e.target.value)}
                            className="text-xs"
                          />
                          <Button
                            size="sm"
                            className="w-full"
                            disabled={!resolJustification.trim() || updateTicket.isPending}
                            onClick={() => updateTicket.mutate({ id: id!, resolutionJustification: resolJustification })}
                          >
                            {ticket.resolutionJustification ? 'Actualizează justificarea' : 'Salvează justificarea'}
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Pause indicator */}
                    {sla.isPaused && (
                      <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                        <PauseCircle size={16} />
                        <span className="text-xs">Pauză — în așteptarea clientului</span>
                      </div>
                    )}
                    {!sla.isPaused && sla.isOutsideBusinessHours && (
                      <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                        <PauseCircle size={16} />
                        <span className="text-xs">Pauză — în afara programului de lucru</span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-dark-muted">Nicio politică SLA aplicabilă.</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Review transition popup — optional comment (public or internal) saved with the transition */}
      <Dialog open={reviewModalOpen} onClose={() => setReviewModalOpen(false)} title="Trimite la review">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-dark-muted">
            Tichetul va fi trimis la aprobarea supervizorului. Vrei să adaugi un comentariu odată cu trimiterea?
          </p>
          <Textarea
            placeholder="Comentariu (opțional)..."
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            className="text-sm"
          />
          <div className="flex gap-2">
            <label className={`flex flex-1 cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm ${reviewCommentType === 'public' ? 'border-blue-400 bg-blue-50 dark:border-blue-500/50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-dark-border'}`}>
              <input
                type="radio"
                name="reviewCommentType"
                checked={reviewCommentType === 'public'}
                onChange={() => setReviewCommentType('public')}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium dark:text-dark-text">Public</span>
                <span className="block text-xs text-gray-500 dark:text-dark-muted">Vizibil și clientului</span>
              </span>
            </label>
            <label className={`flex flex-1 cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm ${reviewCommentType === 'internal' ? 'border-amber-400 bg-amber-50 dark:border-amber-500/50 dark:bg-amber-500/10' : 'border-gray-200 dark:border-dark-border'}`}>
              <input
                type="radio"
                name="reviewCommentType"
                checked={reviewCommentType === 'internal'}
                onChange={() => setReviewCommentType('internal')}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium dark:text-dark-text">Intern</span>
                <span className="block text-xs text-gray-500 dark:text-dark-muted">Doar staff Encorsa</span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
              Anulează
            </Button>
            <Button disabled={transition.isPending} onClick={confirmReviewTransition}>
              {transition.isPending ? 'Se trimite...' : 'Trimite la review'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Client reminder confirmation (sugestie cmuf41x8) */}
      <Dialog open={remindModalOpen} onClose={() => setRemindModalOpen(false)} title="Trimite reminder client">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-dark-muted">
            Se va trimite un email de reamintire către <strong>{ticket.createdBy?.name ?? 'client'}</strong> ({ticket.createdBy?.email ?? '—'}) cu subiectul
            <span className="mt-1 block rounded bg-gray-50 px-2 py-1 text-xs dark:bg-dark-border">
              Reamintire — așteptăm răspunsul dumneavoastră (Tichet #{formatTicketId(ticket.numericId)})
            </span>
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRemindModalOpen(false)}>
              Anulează
            </Button>
            <Button disabled={remindClient.isPending} onClick={() => remindClient.mutate()}>
              {remindClient.isPending ? 'Se trimite...' : 'Trimite reminder'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Email Preview Dialog */}
      {emailPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEmailPreview(null)}>
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-lg bg-white p-6 shadow-lg dark:bg-dark-card dark:shadow-black/30" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 font-semibold dark:text-dark-text">{emailPreview.subject}</h3>
            <div className="prose prose-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailPreview.htmlBody) }} />
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setEmailPreview(null)}>Închide</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
